"use client"

import * as Checkbox from "@radix-ui/react-checkbox"
import { css, f, styled } from "library/styled"
import type { Dispatch, SetStateAction } from "react"
import colors from "styles/colors"
import textStyles from "styles/text"

interface FilterCheckBoxProps {
	category: string
	filterWords: string[]
	setFilterWordsAction: Dispatch<SetStateAction<string[]>>
	setLastUpdatedAction: Dispatch<SetStateAction<"searchValue" | "filterWords">>
	lastUpdated: "searchValue" | "filterWords"
}

export function FilterCheckBox({
	category,
	filterWords,
	setFilterWordsAction,
	setLastUpdatedAction,
}: FilterCheckBoxProps) {
	const isChecked = filterWords.includes(category)

	const handleCheckedChange = (checked: boolean) => {
		if (checked) {
			setFilterWordsAction([...filterWords, category])
		} else {
			setFilterWordsAction(filterWords.filter((word) => word !== category))
		}
		setLastUpdatedAction("filterWords")
	}

	return (
		<CheckboxContainer>
			<CheckboxRoot
				id={category}
				checked={isChecked}
				onCheckedChange={handleCheckedChange}
			>
				<CheckboxIndicator forceMount>
					<svg
						viewBox="0 0 26 26"
						fill="none"
						xmlns="http://www.w3.org/2000/svg"
					>
						<title>check</title>
						<path
							className="unchecked"
							d="M19.2475 2.75252C17.4277 1.6407 15.2887 1 13 1C6.37258 1 1 6.37258 1 13C1 19.6274 6.37258 25 13 25C19.6274 25 25 19.6274 25 13C25 11.1173 24.5664 9.33579 23.7937 7.75"
							stroke="#6E9E8F"
							strokeWidth="0.75"
							strokeLinecap="round"
							strokeLinejoin="round"
							fill="transparent"
						/>
						<path
							className="checked"
							d="M25 13C25 19.6274 19.6274 25 13 25C6.37258 25 1 19.6274 1 13C1 6.37258 6.37258 1 13 1C15.2887 1 17.4277 1.6407 19.2475 2.75252M9.57143 13L13 16.4286L25 4.42857"
							stroke={colors.green300}
							strokeWidth="0.75"
							strokeLinecap="round"
							strokeLinejoin="round"
							fill="transparent"
						/>
					</svg>
				</CheckboxIndicator>
			</CheckboxRoot>
			<CheckboxLabel htmlFor={category}>{category}</CheckboxLabel>
		</CheckboxContainer>
	)
}

const CheckboxRoot = styled(Checkbox.Root, {
	...f.responsive(css`
		width: 16px;
		height: 16px;
		background-color: transparent;
		border: none;
		display: flex;
		align-items: center;
		justify-content: center;
		cursor: pointer;
		position: relative;
		padding: 0;
		margin: 0;

		&:focus {
			outline: none;
		}
	`),
})

const CheckboxIndicator = styled(Checkbox.Indicator, {
	...f.responsive(css`
		position: absolute;
		top: 0;
		left: 0;
		width: 100%;
		height: 100%;
	`),
})

const CheckboxContainer = styled("div", {
	...f.responsive(css`
		position: relative;
		display: flex;
		align-items: center;
		gap: 8px;

		.checked,
		.unchecked {
			transition: opacity 0.3s ease-in-out;
		}

		.checked {
			opacity: 0;
		}

		[data-state="checked"] {
			.checked {
				opacity: 1;
			}

			.unchecked {
				opacity: 0;
			}
		}

		[data-state="unchecked"] {
			.checked {
				opacity: 0;
			}

			.unchecked {
				opacity: 1;
			}
		}
	`),
})

const CheckboxLabel = styled("label", {
	...f.responsive(css`
		${textStyles.titleS};
		color: ${colors.green300};
		cursor: pointer;
	`),
})
